import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import employeeService, { Employee } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { getServerErrorMessage } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import './HrListPage.css';
import { CirclePlus, RefreshCw, SquarePen, Trash2, Users, UserCheck, UserMinus } from 'lucide-react';

const EmployeeListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const { items } = await employeeService.getAll();
      setEmployees(items);
    } catch (err) {
      setError(getServerErrorMessage(err) || t('employee.failedToLoad'));
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Clear the one-shot banner from navigation state so a browser refresh does not replay it.
  useEffect(() => {
    if ((location.state as { message?: string } | null)?.message) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleDelete = async (employee: Employee) => {
    if (!window.confirm(t('employee.confirmDelete', { name: employee.fullName }))) return;
    try {
      await employeeService.delete(employee.id);
      setSuccessMessage(t('employee.deleteSuccess'));
      await load();
    } catch (err) {
      setError(getServerErrorMessage(err) || t('employee.failedToDelete'));
    }
  };

  const formatDate = (value?: string | null) =>
    value ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value)) : '—';

  const columns: GridColumn[] = [
    {
      key: 'employeeCode',
      label: t('employee.employeeCode'),
      sortable: true,
      width: '140px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'fullName',
      label: t('employee.name'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'jobTitle',
      label: t('employee.jobTitle'),
      sortable: true,
      render: (value) => <span className="description-text">{value || '—'}</span>,
    },
    {
      key: 'departmentName',
      label: t('employee.department'),
      sortable: true,
      width: '170px',
      render: (value) => <span className="description-text">{value || '—'}</span>,
    },
    {
      key: 'phone',
      label: t('employee.phone'),
      width: '140px',
      render: (value) => value || '—',
    },
    {
      key: 'hireDate',
      label: t('employee.hireDate'),
      sortable: true,
      width: '140px',
      render: (value) => formatDate(value as string),
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) =>
        value ? (
          <span className="ims-badge ims-badge-approved">{t('common.active')}</span>
        ) : (
          <span className="ims-badge ims-badge-closed">{t('common.inactive')}</span>
        ),
    },
  ];

  const actions: GridAction[] = [
    ...(hasPermission(Permissions.Employees.Edit) ? [{
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (item: any) => navigate(`/hr/employees/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    }] : []),
    ...(hasPermission(Permissions.Employees.Delete) ? [{
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (item: any) => handleDelete(item as Employee),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    }] : []),
  ];

  const activeCount = employees.filter((e) => e.isActive).length;

  return (
    <Layout>
      <div className="container hr-page">
        <div className="header">
          <h1 className="title">{t('employee.title')}</h1>
          <div className="header-actions">
            {hasPermission(Permissions.Employees.Create) && (
              <button
                className="btn-new"
                onClick={() => navigate('/hr/employees/create')}
                title={t('employee.newEmployee')}
              >
                <CirclePlus />
                {t('employee.newEmployee')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-total"><Users size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{employees.length}</span>
              <span className="ims-stat-label">{t('employee.totalEmployees')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-active"><UserCheck size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{activeCount}</span>
              <span className="ims-stat-label">{t('common.active')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon hr-icon-inactive"><UserMinus size={20} /></div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{employees.length - activeCount}</span>
              <span className="ims-stat-label">{t('common.inactive')}</span>
            </div>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={employees}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('employee.noEmployeesFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default EmployeeListPage;
